import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { AggregationType, DimensionType, Granularity, type TimeRangeOption } from '../../types';
import type { DataExportScope } from '../utils/combineDataExportUsage';
import {
  getBreakdownChartSeries,
  getBreakdownGroupBy,
  getBreakdownPreviousRange,
  getBreakdownPreviousTotals,
  getBreakdownSources,
  getDataExportBreakdownView,
  getPendingDestinationSource,
} from '../utils/dataExportBreakdown';
import { DAILY_WINDOW_LIMIT_MS } from '../utils/dataExportRequests';

import { useDataExportSourceUsage } from './useDataExportSourceUsage';

type UseDataExportBreakdownProps = {
  dimension?: DimensionType;
  scope: DataExportScope;
  timeRange?: TimeRangeOption;
  from: number;
  to: number;
  today: Date;
  granularity: Granularity;
  aggregationType: AggregationType;
};

const UNSUPPORTED_DESTINATION_GROUP_BY_KEY = ['data-export-destination-group-by-unsupported'];

export function useDataExportBreakdown({
  dimension,
  scope,
  timeRange,
  from,
  to,
  today,
  granularity,
  aggregationType,
}: UseDataExportBreakdownProps) {
  const groupBy = dimension
    ? Object.fromEntries(
        getBreakdownSources(dimension, scope).map((source) => [source, getBreakdownGroupBy(dimension, source)]),
      )
    : undefined;
  const rankGranularity = granularity === Granularity.MONTHLY ? Granularity.DAILY : granularity;
  const previousRange = getBreakdownPreviousRange({ timeRange, from, to, today });

  const queryClient = useQueryClient();
  const unsupportedDestinationGroupBy = useQuery({
    queryKey: UNSUPPORTED_DESTINATION_GROUP_BY_KEY,
    queryFn: () => false,
    enabled: false,
    gcTime: Infinity,
    staleTime: Infinity,
  });
  const isDestination = dimension === DimensionType.DESTINATION_ID;
  const probeSource = isDestination && dimension ? getBreakdownSources(dimension, scope)[0] : undefined;
  const isKnownUnsupported = isDestination && unsupportedDestinationGroupBy.data === true;
  const probe = useDataExportSourceUsage({
    from,
    to: rankGranularity === Granularity.DAILY ? Math.min(to, from + DAILY_WINDOW_LIMIT_MS) : to,
    aggregationType: AggregationType.INCREMENTAL,
    granularity: rankGranularity,
    groupBy: probeSource && dimension ? { [probeSource]: [dimension] } : undefined,
    enabled: !!probeSource && !isKnownUnsupported,
  });
  const probeState = probeSource ? probe[probeSource] : undefined;
  const isUnsupported = isKnownUnsupported || !!probeState?.isUnsupportedGroupBy;

  useEffect(() => {
    if (probeState?.isUnsupportedGroupBy) {
      queryClient.setQueryDefaults(UNSUPPORTED_DESTINATION_GROUP_BY_KEY, { gcTime: Infinity });
      queryClient.setQueryData(UNSUPPORTED_DESTINATION_GROUP_BY_KEY, true);
    }
  }, [probeState?.isUnsupportedGroupBy, queryClient]);

  const enabled = !!dimension && (!isDestination || (!!probeState?.data && !isUnsupported));

  const ranking = useDataExportSourceUsage({
    from,
    to,
    aggregationType: AggregationType.INCREMENTAL,
    granularity: rankGranularity,
    groupBy,
    enabled,
  });
  const previous = useDataExportSourceUsage({
    ...previousRange,
    aggregationType: AggregationType.INCREMENTAL,
    granularity: rankGranularity,
    groupBy,
    enabled,
  });
  const charts = useDataExportSourceUsage({ from, to, aggregationType, granularity, groupBy, enabled });

  if (!dimension) {
    return undefined;
  }
  if (isUnsupported || probeState?.isError) {
    const status = isUnsupported ? 'notYetAvailable' : 'error';
    return { view: { status }, pendingDestinations: undefined, previous: undefined, charts: { status } } as const;
  }
  const pending = getPendingDestinationSource(dimension, scope, ranking);
  const view = getDataExportBreakdownView({ dimension, scope, sources: ranking, pending });
  return {
    view,
    pendingDestinations: pending,
    previous: getBreakdownPreviousTotals({ dimension, scope, sources: previous }),
    charts: getBreakdownChartSeries({
      dimension,
      scope,
      sources: charts,
      keys: view.status === 'ready' ? view.ranked.map(({ key }) => key) : [],
      aggregationType,
    }),
  };
}

export type DataExportBreakdown = NonNullable<ReturnType<typeof useDataExportBreakdown>>;
